/**
 * How this was derived.
 *
 * Renders the server's derivation record and research coverage — what was
 * planned, what was searched, what held, and every way the search was narrower
 * than intended.
 *
 * THE SENTENCE THIS MODAL EXISTS TO GET RIGHT: `none found after search` means
 * the bounded search found none. It does not mean none exists. Those two
 * readings differ by everything, and the second one is what a reader supplies
 * for themselves unless the first is stated. It is stated here, in full, next to
 * the count.
 *
 * WHAT NEVER APPEARS: prompts, private query text, provider credentials, or any
 * generated answer. The coverage record is counts and warnings — a description
 * of the search, not a transcript of it.
 */

import React from 'react';
import { Text, View } from 'react-native';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import type { ResearchCoverageView } from '@/lib/strategic-analysis/types';
import { labelForField } from './field-groups';

export interface DerivationRecord {
  methodologyVersion?: string;
  /** Registered axes, drivers, factors or pathways this analysis may use. */
  controlledElements?: string[];
  /** Named thresholds and how they were applied. */
  thresholds?: { label: string; value: string }[];
  transformations?: string[];
  /** Why a fallback was taken — e.g. quadrant coverage failed. */
  fallbackReason?: string;
  freshness?: string;
  explanation?: string;
}

const ELEMENT_STATE_LABELS: Record<ResearchCoverageView['elements'][number]['state'], string> = {
  supported: 'Supported by accepted observations',
  conflicted: 'Conflicting evidence',
  // RETRIEVED IS NOT SUPPORTED. Sources came back and nothing has read them
  // against the conclusion; saying "supported" here was a claim about content
  // derived from the fact that a search returned something.
  retrieved: 'Sources retrieved — relation not yet established',
  // A STATEMENT ABOUT US, not about the market. The sources exist and were
  // fetched; no approved decision permits sending them to a model. Rendering
  // this as "unknown" would blame the market for a licensing gap.
  policy_limited: 'Source retrieved, but policy does not permit AI processing',
  unknown: 'Unknown — searched, nothing sufficient found',
  skipped: 'Not searched',
  budget_exhausted: 'Not searched — research budget reached',
};

interface MethodologyModalProps {
  visible: boolean;
  onClose: () => void;
  coverage: ResearchCoverageView | null;
  derivation: DerivationRecord | null;
}

export function MethodologyModal({ visible, onClose, coverage, derivation }: MethodologyModalProps) {
  const colors = useThemeColors();

  return (
    <AppModal
      visible={visible}
      onClose={onClose}
      title="How this was derived"
      footer={
        <AppModal.Actions>
          <ModalAction label="Close" onPress={onClose} variant="neutral" />
        </AppModal.Actions>
      }
    >
      <View className="gap-5">
        {derivation?.explanation && (
          <Text className="text-sm leading-5" style={{ color: colors.text.primary }}>
            {derivation.explanation}
          </Text>
        )}

        {derivation?.methodologyVersion && (
          <Row label="Methodology version" value={`v${derivation.methodologyVersion}`} colors={colors} />
        )}

        {derivation?.controlledElements && derivation.controlledElements.length > 0 && (
          <Section title="What this analysis is allowed to score" colors={colors}>
            <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
              {derivation.controlledElements.map(labelForField).join(' · ')}
            </Text>
            <Text className="text-xs leading-4" style={{ color: colors.text.tertiary }}>
              These are fixed in code. The model can find evidence and propose readings within them;
              it cannot add a new one or move a threshold.
            </Text>
          </Section>
        )}

        {derivation?.thresholds && derivation.thresholds.length > 0 && (
          <Section title="Thresholds applied" colors={colors}>
            {derivation.thresholds.map((t) => (
              <Row key={t.label} label={t.label} value={t.value} colors={colors} />
            ))}
          </Section>
        )}

        {derivation?.transformations && derivation.transformations.length > 0 && (
          <Section title="Transformations" colors={colors}>
            {derivation.transformations.map((t, i) => (
              <Text key={`transform-${i}`} className="text-sm leading-5" style={{ color: colors.text.secondary }}>
                {t}
              </Text>
            ))}
          </Section>
        )}

        {derivation?.fallbackReason && (
          <Section title="Why a fallback was used" colors={colors}>
            <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
              {derivation.fallbackReason}
            </Text>
          </Section>
        )}

        {derivation?.freshness && (
          <Section title="Freshness" colors={colors}>
            <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
              {derivation.freshness}
            </Text>
          </Section>
        )}

        {coverage && (
          <>
            <Section title="What was searched" colors={colors}>
              <Row label="Search rounds" value={String(coverage.rounds)} colors={colors} />
              <Row label="Search calls" value={String(coverage.searchCalls)} colors={colors} />
              <Row
                label="Independent source groups"
                value={String(coverage.independenceGroups)}
                colors={colors}
              />
              <Text className="text-xs leading-4" style={{ color: colors.text.tertiary }}>
                Sources are counted in independent groups. The same page returned by two search
                providers is one source, not two.
              </Text>
              <Row
                label="Primary sources"
                value={`${coverage.primarySourceCoverage.achieved} of ${coverage.primarySourceCoverage.targeted} targeted`}
                colors={colors}
              />
            </Section>

            <Section title="Search for evidence against the conclusions" colors={colors}>
              <Row
                label="Elements targeted"
                value={String(coverage.counterEvidenceCoverage.targeted)}
                colors={colors}
              />
              <Row
                label="Counter-evidence found"
                value={String(coverage.counterEvidenceCoverage.completed)}
                colors={colors}
              />
              <Row
                label="None found after search"
                value={String(coverage.counterEvidenceCoverage.noneFound)}
                colors={colors}
              />
              <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
                None found after search means this bounded search found none. It is not evidence that
                none exists.
              </Text>
              {coverage.counterEvidenceCoverage.limited > 0 && (
                <Text className="text-sm leading-5" style={{ color: colors.amber.main }}>
                  {coverage.counterEvidenceCoverage.limited} element(s) could not be fully searched
                  because a provider failed, a filter could not be applied, or the budget was reached.
                </Text>
              )}
            </Section>

            <Section title="Coverage by element" colors={colors}>
              {coverage.elements.map((element) => (
                <View key={`element-${element.methodologyElement}`} className="flex-row items-start gap-2">
                  <Text
                    numberOfLines={2}
                    className="flex-1 text-sm"
                    style={{ color: colors.text.primary }}
                  >
                    {labelForField(element.methodologyElement)}
                  </Text>
                  <Text
                    numberOfLines={2}
                    className="text-sm text-right shrink-0"
                    style={{
                      maxWidth: '55%',
                      color: element.state === 'supported' ? colors.success : colors.text.secondary,
                    }}
                  >
                    {ELEMENT_STATE_LABELS[element.state]}
                  </Text>
                </View>
              ))}
            </Section>

            {coverage.budgetExhausted && (
              <View className="rounded-xl p-3.5" style={{ backgroundColor: colors.amber.light }}>
                <Text className="text-sm leading-5" style={{ color: colors.amber.main }}>
                  The research budget for this report was reached before every element was searched.
                  Anything left unsearched is shown as unknown rather than assumed.
                </Text>
              </View>
            )}

            {coverage.providerWarnings.length > 0 && (
              <Section title="Limits on this search" colors={colors}>
                {coverage.providerWarnings.map((warning, i) => (
                  <Text
                    key={`warning-${i}`}
                    className="text-sm leading-5"
                    style={{ color: colors.text.secondary }}
                  >
                    {warning.message}
                  </Text>
                ))}
              </Section>
            )}

            <Text className="text-xs leading-4" style={{ color: colors.text.tertiary }}>
              Retrieved {new Date(coverage.retrievedAt).toLocaleString()}
            </Text>
          </>
        )}
      </View>
    </AppModal>
  );
}

function Section({
  title,
  colors,
  children,
}: {
  title: string;
  colors: ReturnType<typeof useThemeColors>;
  children: React.ReactNode;
}) {
  return (
    <View className="gap-2">
      <Text className="text-xs font-semibold uppercase" style={{ color: colors.text.tertiary }}>
        {title}
      </Text>
      {children}
    </View>
  );
}

function Row({
  label,
  value,
  colors,
}: {
  label: string;
  value: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View className="flex-row items-center gap-2">
      <Text numberOfLines={1} className="flex-1 text-sm" style={{ color: colors.text.secondary }}>
        {label}
      </Text>
      <Text numberOfLines={1} className="text-sm font-semibold shrink-0" style={{ color: colors.text.primary }}>
        {value}
      </Text>
    </View>
  );
}
